// ── Root Locus Method — Control Systems ──────────────────────────────────────

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Shared SVG defs snippet (arrows, markers) ─────────────────────
function SvgDefs() {
  return (
    <defs>
      <marker id="arrRed" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <polygon points="0 0, 7 4, 0 8" fill="#c0392b"/>
      </marker>
      <marker id="arrBlue" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <polygon points="0 0, 7 4, 0 8" fill="#2E75B6"/>
      </marker>
      <marker id="arrOrange" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <polygon points="0 0, 7 4, 0 8" fill="#e07b00"/>
      </marker>
      <marker id="arrGreen" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <polygon points="0 0, 7 4, 0 8" fill="#27ae60"/>
      </marker>
    </defs>
  );
}

// ── Helpers ───────────────────────────────────────────────────────
const POLE_COLOR   = '#c0392b';
const ZERO_COLOR   = '#2E75B6';
const LOCUS_COLOR  = '#c0392b';
const ASYM_COLOR   = '#e07b00';
const STAB_RED     = '#ffebee';
const INFO_BLUE    = '#eaf3ff';
const INFO_ORANGE  = '#fff7e6';

function Pole({ cx, cy, size = 13 }) {
  return (
    <text x={cx} y={cy + size * 0.4} textAnchor="middle" fontSize={size}
      fill={POLE_COLOR} fontWeight="700">×</text>
  );
}
function Zero({ cx, cy, size = 12 }) {
  return <circle cx={cx} cy={cy} r={size * 0.45} fill="none" stroke={ZERO_COLOR} strokeWidth="2"/>;
}

function SPlaneAxes({ x0, y0, w, h, scaleX = 50, scaleY = 50, xRange = [-4, 2], yRange = [-3, 3] }) {
  const toX = (r) => x0 + (r - xRange[0]) / (xRange[1] - xRange[0]) * w;
  const toY = (i) => y0 + (1 - (i - yRange[0]) / (yRange[1] - yRange[0])) * h;
  const originX = toX(0);
  const originY = toY(0);
  const ticks = { x: [], y: [] };
  for (let v = Math.ceil(xRange[0]); v <= Math.floor(xRange[1]); v++) ticks.x.push(v);
  for (let v = Math.ceil(yRange[0]); v <= Math.floor(yRange[1]); v++) ticks.y.push(v);
  return (
    <g>
      {ticks.x.map(v => (
        <g key={`tx${v}`}>
          <line x1={toX(v)} y1={y0} x2={toX(v)} y2={y0 + h} stroke="#f0f0ee" strokeWidth="1"/>
          {v !== 0 && <text x={toX(v)} y={y0 + h + 13} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>}
        </g>
      ))}
      {ticks.y.map(v => (
        <g key={`ty${v}`}>
          <line x1={x0} y1={toY(v)} x2={x0 + w} y2={toY(v)} stroke="#f0f0ee" strokeWidth="1"/>
          {v !== 0 && <text x={x0 - 5} y={toY(v) + 4} textAnchor="end" fontSize="10" fill="#bbb">
            {v > 0 ? `+${v}j` : `${v}j`}
          </text>}
        </g>
      ))}
      {/* Imaginary axis — stability boundary */}
      <line x1={originX} y1={y0} x2={originX} y2={y0 + h} stroke="#c0392b" strokeWidth="1.8" strokeDasharray="6 3" opacity="0.55"/>
      {/* Real axis */}
      <line x1={x0} y1={originY} x2={x0 + w} y2={originY} stroke="#ccc" strokeWidth="1.4"/>
      {/* Labels */}
      <text x={x0 + w + 6} y={originY + 4} fontSize="11" fill="#888">σ</text>
      <text x={originX + 4} y={y0 + 12} fontSize="11" fill="#888">jω</text>
      <text x={originX + 4} y={y0 + 24} fontSize="9" fill={POLE_COLOR} opacity="0.7">boundary</text>
    </g>
  );
}

// ── Diagram 1: S-Plane Anatomy — poles, zeros, regions ───────────
function DiagramSPlaneAnatomy() {
  const W = 900, H = 400;
  const px = 90, py = 30, pw = 540, ph = 340;

  const xRange = [-5, 3], yRange = [-3, 3];
  const toX = (r) => px + (r - xRange[0]) / (xRange[1] - xRange[0]) * pw;
  const toY = (i) => py + (1 - (i - yRange[0]) / (yRange[1] - yRange[0])) * ph;
  const originX = toX(0), originY = toY(0);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The s-Plane — Poles, Zeros, Stability Regions, and Performance Contours</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>

        {/* Unstable half-plane shading */}
        <rect x={toX(0)} y={py} width={pw - (toX(0)-px)} height={ph} fill="#ffebee" opacity="0.45"/>
        <text x={toX(1.2)} y={toY(0.3)} textAnchor="middle" fontSize="13" fill={POLE_COLOR} fontWeight="700" opacity="0.6">UNSTABLE</text>
        <text x={toX(1.2)} y={toY(0.3)+16} textAnchor="middle" fontSize="10" fill={POLE_COLOR} opacity="0.5">Re(s) &gt; 0</text>

        {/* Stable half-plane label */}
        <text x={toX(-2.5)} y={toY(0.3)} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700" opacity="0.6">STABLE</text>
        <text x={toX(-2.5)} y={toY(0.3)+16} textAnchor="middle" fontSize="10" fill="#27ae60" opacity="0.5">Re(s) &lt; 0</text>

        {/* Constant damping lines (radial from origin) */}
        {[0.3, 0.5, 0.7].map((zeta, i) => {
          const angle = Math.acos(zeta);
          const len = 2.5;
          const dx = -Math.cos(angle) * len, dy = Math.sin(angle) * len;
          const colors = ['#e07b00','#2E75B6','#27ae60'];
          return (
            <g key={zeta}>
              <line
                x1={originX} y1={originY}
                x2={toX(dx)} y2={toY(dy)}
                stroke={colors[i]} strokeWidth="1.2" strokeDasharray="5 3" opacity="0.55"/>
              <line
                x1={originX} y1={originY}
                x2={toX(dx)} y2={toY(-dy)}
                stroke={colors[i]} strokeWidth="1.2" strokeDasharray="5 3" opacity="0.55"/>
              <text x={toX(dx) - 6} y={toY(dy) - 6} fontSize="9" fill={colors[i]} opacity="0.85">ζ={zeta}</text>
            </g>
          );
        })}

        {/* Constant natural frequency arc */}
        {[1.5, 2.5].map((wn, i) => (
          <ellipse key={wn}
            cx={originX} cy={originY}
            rx={toX(wn) - originX} ry={originY - toY(wn)}
            fill="none" stroke="#9b59b6" strokeWidth="1" strokeDasharray="4 4" opacity="0.4"/>
        ))}
        <text x={toX(-1.3)} y={toY(0.85)} fontSize="9" fill="#9b59b6" opacity="0.8">ωₙ=1.5</text>
        <text x={toX(-2.2)} y={toY(1.45)} fontSize="9" fill="#9b59b6" opacity="0.8">ωₙ=2.5</text>

        {/* Axes */}
        <SPlaneAxes x0={px} y0={py} w={pw} h={ph} xRange={xRange} yRange={yRange}/>

        {/* Example poles and zeros */}
        <Pole cx={toX(-1)} cy={toY(1.5)}/>
        <Pole cx={toX(-1)} cy={toY(-1.5)}/>
        <Pole cx={toX(-3)} cy={toY(0)}/>
        <Zero cx={toX(-2)} cy={toY(0)}/>
        <Zero cx={toX(0.5)} cy={toY(0)}/>

        {/* Annotations */}
        <text x={toX(-1)+14} y={toY(1.5)-8} fontSize="10" fill={POLE_COLOR} fontWeight="700">Complex conjugate poles</text>
        <text x={toX(-1)+14} y={toY(1.5)+4} fontSize="9" fill={POLE_COLOR}>σ = −ζωₙ, ω = ωₙ√(1−ζ²)</text>
        <text x={toX(-3)+14} y={toY(0)+4} fontSize="10" fill={POLE_COLOR} fontWeight="700">Real pole (σ = −3)</text>
        <text x={toX(-2)+14} y={toY(0)+14} fontSize="10" fill={ZERO_COLOR} fontWeight="700">Finite zero</text>
        <text x={toX(0.5)+8} y={toY(0)-8} fontSize="10" fill={ZERO_COLOR} fontWeight="700">RHP zero</text>
        <text x={toX(0.5)+8} y={toY(0)+4} fontSize="9" fill={ZERO_COLOR}>(non-minimum phase)</text>

        {/* Legend panel */}
        <rect x={650} y={py} width={235} height={ph} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={767} y={py+18} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">s-Plane Legend</text>

        {[
          { y:py+30, bg:'#fff5f5', bc:POLE_COLOR, sym:'×', symColor:POLE_COLOR, lines:['Open-loop pole','Marked with ×; root locus','branch starts here (K=0)'] },
          { y:py+86, bg:'#eaf3ff', bc:ZERO_COLOR, sym:'○', symColor:ZERO_COLOR, lines:['Open-loop zero','Marked with ○; root locus','branch ends here (K→∞)'] },
          { y:py+142, bg:INFO_ORANGE, bc:'#e07b00', lines:['Damping rays (ζ)','Constant damping ratio lines','from origin; angle = cos⁻¹(ζ)'] },
          { y:py+198, bg:'#f5eeff', bc:'#9b59b6', lines:['Freq. arcs (ωₙ)','Semicircles of constant natural','frequency centred at origin'] },
          { y:py+254, bg:'#fff5f5', bc:POLE_COLOR, lines:['Stability boundary','Imaginary axis (Re=0)','Poles left = stable; right = unstable'] },
        ].map(({ y, bg, bc, sym, symColor, lines }) => (
          <g key={y}>
            <rect x={656} y={y} width={223} height={54} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {sym && <text x={666} y={y+22} fontSize="16" fill={symColor} fontWeight="700">{sym}</text>}
            {lines.map((t, i) => (
              <text key={i} x={sym ? 680 : 662} y={y + 16 + i*16} fontSize={i===0?'12':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The s-plane is the complex plane of the Laplace variable s = σ + jω. Closed-loop stability requires all poles to lie in the left-half plane (Re(s) &lt; 0). Diagonal rays from the origin are lines of constant damping ratio ζ; semicircles are lines of constant natural frequency ωₙ. The root locus is a map of how closed-loop poles move through this plane as gain K increases.</div>
    </div>
  );
}

// ── Diagram 2: Construction Rules — 5-panel reference card ────────
function DiagramConstructionRules() {
  const W = 900, H = 500;
  const cols = [
    { x:10,  w:168, title:'Rule 1 — Start / End', color:POLE_COLOR },
    { x:186, w:168, title:'Rule 2 — Branches', color:'#2E75B6' },
    { x:362, w:168, title:'Rule 3 — Real-Axis', color:'#27ae60' },
    { x:538, w:168, title:'Rule 4 — Asymptotes', color:ASYM_COLOR },
    { x:714, w:172, title:'Rule 5 — Breakaway', color:'#9b59b6' },
  ];

  // Mini s-plane helper
  const mini = (col) => {
    const { x, w } = col;
    const cx = x + w/2, cy = 260;
    const toX = (r) => cx + r * 38;
    const toY = (i) => cy - i * 38;
    return { cx, cy, toX, toY };
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Root Locus Construction Rules — Five Foundational Rules</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>

        {cols.map((col, ci) => {
          const { x, w, title, color } = col;
          const { cx, cy, toX, toY } = mini(col);

          return (
            <g key={ci}>
              <rect x={x} y={10} width={w} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
              {/* Title */}
              <rect x={x} y={10} width={w} height={34} rx={5} fill={color} opacity="0.12"/>
              <text x={x+w/2} y={30} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{title}</text>

              {ci === 0 && (
                // Rule 1: Start at poles (K=0), end at zeros (K→∞)
                <g>
                  <Pole cx={toX(-1.5)} cy={toY(1)} size={16}/>
                  <Pole cx={toX(-1.5)} cy={toY(-1)} size={16}/>
                  <Pole cx={toX(-2.5)} cy={toY(0)} size={16}/>
                  <Zero cx={toX(-1)} cy={toY(0)} size={13}/>

                  {/* Axes */}
                  <line x1={x+14} y1={cy} x2={x+w-14} y2={cy} stroke="#ddd" strokeWidth="1"/>
                  <line x1={cx} y1={cy-120} x2={cx} y2={cy+120} stroke="#ddd" strokeWidth="1"/>

                  {/* Locus arrows from poles */}
                  <line x1={toX(-1.5)} y1={toY(1)} x2={toX(-1.5)+14} y2={toY(1)-22}
                    stroke={POLE_COLOR} strokeWidth="2" markerEnd="url(#arrRed)"/>
                  <line x1={toX(-1.5)} y1={toY(-1)} x2={toX(-1.5)+14} y2={toY(-1)+22}
                    stroke={POLE_COLOR} strokeWidth="2" markerEnd="url(#arrRed)"/>
                  <line x1={toX(-2.5)} y1={toY(0)} x2={toX(-2.5)+24} y2={toY(0)}
                    stroke={POLE_COLOR} strokeWidth="2" markerEnd="url(#arrRed)"/>

                  <text x={cx} y={cy+150} textAnchor="middle" fontSize="10" fill="#555">K=0: start at poles (×)</text>
                  <text x={cx} y={cy+164} textAnchor="middle" fontSize="10" fill="#555">K→∞: end at zeros (○)</text>
                  <text x={cx} y={cy+178} textAnchor="middle" fontSize="10" fill="#555">or go to ∞ along asymptotes</text>
                </g>
              )}

              {ci === 1 && (
                // Rule 2: #branches = #poles; complex branches in conjugate pairs
                <g>
                  <line x1={x+14} y1={cy} x2={x+w-14} y2={cy} stroke="#ddd" strokeWidth="1"/>
                  <line x1={cx} y1={cy-120} x2={cx} y2={cy+120} stroke="#ddd" strokeWidth="1"/>

                  <Pole cx={toX(-0.5)} cy={toY(1.5)} size={14}/>
                  <Pole cx={toX(-0.5)} cy={toY(-1.5)} size={14}/>
                  <Pole cx={toX(-2)} cy={toY(0)} size={14}/>

                  {/* Conjugate pair branch */}
                  <path d={`M ${toX(-0.5)},${toY(1.5)} C ${toX(0.3)},${toY(2)} ${toX(0.5)},${toY(2.5)} ${toX(0.2)},${toY(3)}`}
                    fill="none" stroke="#2E75B6" strokeWidth="2" markerEnd="url(#arrBlue)"/>
                  <path d={`M ${toX(-0.5)},${toY(-1.5)} C ${toX(0.3)},${toY(-2)} ${toX(0.5)},${toY(-2.5)} ${toX(0.2)},${toY(-3)}`}
                    fill="none" stroke="#2E75B6" strokeWidth="2" markerEnd="url(#arrBlue)"/>
                  {/* Real branch */}
                  <line x1={toX(-2)} y1={toY(0)} x2={toX(-3.5)} y2={toY(0)}
                    stroke="#2E75B6" strokeWidth="2" markerEnd="url(#arrBlue)"/>

                  {/* Symmetry axis */}
                  <line x1={x+14} y1={cy} x2={x+w-14} y2={cy} stroke="#2E75B6" strokeWidth="1" strokeDasharray="4 2" opacity="0.4"/>

                  <text x={cx} y={cy+150} textAnchor="middle" fontSize="10" fill="#555"># branches = # poles</text>
                  <text x={cx} y={cy+164} textAnchor="middle" fontSize="10" fill="#555">Complex branches appear</text>
                  <text x={cx} y={cy+178} textAnchor="middle" fontSize="10" fill="#555">as conjugate pairs (symmetric</text>
                  <text x={cx} y={cy+192} textAnchor="middle" fontSize="10" fill="#555">about the real axis)</text>
                </g>
              )}

              {ci === 2 && (
                // Rule 3: Real-axis segment to the left of odd number of real poles+zeros
                <g>
                  <line x1={x+14} y1={cy} x2={x+w-14} y2={cy} stroke="#ddd" strokeWidth="1.5"/>
                  <line x1={cx} y1={cy-120} x2={cx} y2={cy+120} stroke="#ddd" strokeWidth="1"/>

                  {/* Poles and zeros on real axis */}
                  <Pole cx={toX(-3)} cy={toY(0)} size={14}/>
                  <Pole cx={toX(-1.5)} cy={toY(0)} size={14}/>
                  <Zero cx={toX(-2)} cy={toY(0)} size={11}/>
                  <Zero cx={toX(0.5)} cy={toY(0)} size={11}/>

                  {/* Locus segments highlighted */}
                  <line x1={toX(-1.5)} y1={cy} x2={toX(-2)} y2={cy} stroke="#27ae60" strokeWidth="5" opacity="0.4"/>
                  <line x1={toX(-3)} y1={cy} x2={x+14} y2={cy} stroke="#27ae60" strokeWidth="5" opacity="0.4"/>

                  {/* Counts of poles+zeros to the right */}
                  <text x={toX(-0.5)} y={cy-12} textAnchor="middle" fontSize="9" fill="#888">count=2 (even)</text>
                  <text x={toX(-0.5)} y={cy-2} textAnchor="middle" fontSize="9" fill="#888">NOT on locus</text>
                  <text x={toX(-1.75)} y={cy+16} textAnchor="middle" fontSize="9" fill="#27ae60">count=3 (odd)</text>
                  <text x={toX(-3.8)} y={cy+16} textAnchor="middle" fontSize="9" fill="#27ae60">count=5 (odd)</text>

                  <text x={cx} y={cy+150} textAnchor="middle" fontSize="10" fill="#555">Segment on real axis if</text>
                  <text x={cx} y={cy+164} textAnchor="middle" fontSize="10" fill="#555">ODD number of real</text>
                  <text x={cx} y={cy+178} textAnchor="middle" fontSize="10" fill="#555">poles + zeros lie to its</text>
                  <text x={cx} y={cy+192} textAnchor="middle" fontSize="10" fill="#555">right on the real axis</text>
                </g>
              )}

              {ci === 3 && (
                // Rule 4: Asymptotes — angle and centroid
                <g>
                  <line x1={x+14} y1={cy} x2={x+w-14} y2={cy} stroke="#ddd" strokeWidth="1"/>
                  <line x1={cx} y1={cy-130} x2={cx} y2={cy+130} stroke="#ddd" strokeWidth="1"/>

                  <Pole cx={toX(-2)} cy={toY(0)} size={13}/>
                  <Pole cx={toX(-0.5)} cy={toY(1)} size={13}/>
                  <Pole cx={toX(-0.5)} cy={toY(-1)} size={13}/>
                  <Zero cx={toX(-1)} cy={toY(0)} size={10}/>

                  {/* Centroid */}

                  <circle cx={toX((-2-0.5-0.5+1)/2)} cy={toY(0)} r={4} fill={ASYM_COLOR}/>
                  <text x={toX((-2-0.5-0.5+1)/2)+6} y={cy-8} fontSize="9" fill={ASYM_COLOR} fontWeight="700">σₐ = −1</text>

                  {/* Asymptote lines at 60°, 180°, 300° from centroid */}
                  {[60, 180, 300].map(deg => {
                    const rad = deg * Math.PI / 180;
                    const len = 90;
                    const startX = toX(-1), startY = toY(0);
                    return (
                      <line key={deg}
                        x1={startX} y1={startY}
                        x2={startX + Math.cos(rad) * len}
                        y2={startY - Math.sin(rad) * len}
                        stroke={ASYM_COLOR} strokeWidth="1.5" strokeDasharray="5 3" opacity="0.7"
                        markerEnd="url(#arrOrange)"/>
                    );
                  })}
                  <text x={cx} y={cy+148} textAnchor="middle" fontSize="10" fill="#555">Angles: (2k+1)×180° / (P−Z)</text>
                  <text x={cx} y={cy+162} textAnchor="middle" fontSize="10" fill="#555">Centroid: σₐ=(Σpoles−Σzeros)</text>
                  <text x={cx} y={cy+176} textAnchor="middle" fontSize="10" fill="#555">  / (P−Z)</text>
                  <text x={cx} y={cy+190} textAnchor="middle" fontSize="10" fill={ASYM_COLOR}>P−Z branches → ∞</text>
                </g>
              )}

              {ci === 4 && (
                // Rule 5: Breakaway / break-in points
                <g>
                  <line x1={x+14} y1={cy} x2={x+w-14} y2={cy} stroke="#ddd" strokeWidth="1.5"/>
                  <line x1={cx} y1={cy-120} x2={cx} y2={cy+120} stroke="#ddd" strokeWidth="1"/>

                  <Pole cx={toX(-1)} cy={toY(0)} size={14}/>
                  <Pole cx={toX(-3)} cy={toY(0)} size={14}/>

                  {/* Locus on real axis between the poles */}
                  <line x1={toX(-1)} y1={cy} x2={toX(-3)} y2={cy} stroke={POLE_COLOR} strokeWidth="3" opacity="0.35"/>

                  {/* Breakaway point at -2 */}
                  <circle cx={toX(-2)} cy={toY(0)} r={6} fill="#9b59b6"/>
                  <text x={toX(-2)} y={cy-14} textAnchor="middle" fontSize="9" fill="#9b59b6" fontWeight="700">Breakaway s=−2</text>

                  {/* Branches leaving breakaway upward/downward */}
                  <path d={`M ${toX(-2)},${toY(0)} C ${toX(-2)},${toY(0.8)} ${toX(-1.5)},${toY(1.5)} ${toX(-1)},${toY(2)}`}
                    fill="none" stroke="#9b59b6" strokeWidth="2" markerEnd="url(#arrBlue)"/>
                  <path d={`M ${toX(-2)},${toY(0)} C ${toX(-2)},${toY(-0.8)} ${toX(-1.5)},${toY(-1.5)} ${toX(-1)},${toY(-2)}`}
                    fill="none" stroke="#9b59b6" strokeWidth="2" markerEnd="url(#arrBlue)"/>

                  <text x={cx} y={cy+150} textAnchor="middle" fontSize="10" fill="#555">Breakaway: locus leaves</text>
                  <text x={cx} y={cy+164} textAnchor="middle" fontSize="10" fill="#555">real axis (max K on axis)</text>
                  <text x={cx} y={cy+178} textAnchor="middle" fontSize="10" fill="#555">Solve: dK/ds = 0  or</text>
                  <text x={cx} y={cy+192} textAnchor="middle" fontSize="10" fill="#555">Σ 1/(s−pᵢ) = Σ 1/(s−zᵢ)</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>The five core construction rules allow the root locus to be sketched by hand. Rule 1 fixes start and end points. Rule 2 counts branches. Rule 3 identifies real-axis segments. Rule 4 gives the asymptotes for branches that go to infinity. Rule 5 finds breakaway/break-in points where the locus leaves or returns to the real axis.</div>
    </div>
  );
}

// ── Diagram 3: Classic worked example — G(s)=K/[s(s+2)(s+4)] ────
function DiagramWorkedExample() {
  const W = 900, H = 420;
  const px = 80, py = 30, pw = 510, ph = 360;
  const xRange = [-6, 2], yRange = [-3.5, 3.5];
  const toX = r => px + (r - xRange[0]) / (xRange[1] - xRange[0]) * pw;
  const toY = i => py + (1 - (i - yRange[0]) / (yRange[1] - yRange[0])) * ph;
  const originX = toX(0), originY = toY(0);

  // Locus path: three branches
  // Branch 1: from s=0 → along -real axis → breakaway at ~-0.85 → up/down curves → asymptotes 60°/300°
  // Branch 2: from s=-2 → right to breakaway at -0.85, then folds back → meet with branch 3 at break-in ~-3.15 → leftward
  // Branch 3: from s=-4 → leftward along real axis, meets branch 2 at break-in ~-3.15, then up/down

  // Branch from s=0 leftward to breakaway ~-0.845
  const brkX = toX(-0.845), brkY = toY(0);
  // Branch from s=-2 rightward to breakaway
  // Break-in at ~-3.15
  const bkiX = toX(-3.15), bkiY = toY(0);

  // Asymptote centroid: (0-2-4)/3 = -2
  const centX = toX(-2), centY = toY(0);

  // Imaginary axis crossing (Routh): K_c where poles hit jω ~ ±2.83
  const jcX = toX(0), jcYp = toY(2.83), jcYm = toY(-2.83);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Worked Example — G(s) = K / [s(s+2)(s+4)]</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>

        {/* Plot background */}
        <rect x={px} y={py} width={pw} height={ph} rx={4} fill="#fff" stroke="#f0cece"/>
        {/* Unstable shading */}
        <rect x={originX} y={py} width={px+pw-originX} height={ph} fill="#ffebee" opacity="0.3"/>

        <SPlaneAxes x0={px} y0={py} w={pw} h={ph} xRange={xRange} yRange={yRange}/>

        {/* ── ASYMPTOTES (3 of them: 60°, 180°, 300°) ────────────── */}
        {[60, 180, 300].map(deg => {
          const rad = deg * Math.PI / 180;
          const len = 210;
          return (
            <line key={deg}
              x1={centX} y1={centY}
              x2={centX + Math.cos(rad) * len}
              y2={centY - Math.sin(rad) * len}
              stroke={ASYM_COLOR} strokeWidth="1.5" strokeDasharray="7 4" opacity="0.65"/>
          );
        })}
        <circle cx={centX} cy={centY} r={5} fill={ASYM_COLOR}/>
        <text x={centX+7} y={centY-9} fontSize="10" fill={ASYM_COLOR} fontWeight="700">Centroid σₐ=−2</text>
        <text x={centX-44} y={centY+14} fontSize="9" fill={ASYM_COLOR}>asymptotes 60°,180°,300°</text>

        {/* ── ROOT LOCUS BRANCHES ─────────────────────────────────── */}
        {/* Branch 1+2 upper: breakaway → curved up → asymptote 60° */}
        <path d={`M ${brkX},${brkY}
          C ${brkX},${toY(0.6)}
            ${toX(-1.1)},${toY(1.4)}
            ${toX(-1.5)},${toY(2)}
          C ${toX(-1.8)},${toY(2.5)}
            ${toX(-2)},${toY(2.8)}
            ${toX(-2.3)},${toY(3.2)}`}
          fill="none" stroke={LOCUS_COLOR} strokeWidth="2.5"/>
        {/* Branch 1+2 lower: mirror */}
        <path d={`M ${brkX},${brkY}
          C ${brkX},${toY(-0.6)}
            ${toX(-1.1)},${toY(-1.4)}
            ${toX(-1.5)},${toY(-2)}
          C ${toX(-1.8)},${toY(-2.5)}
            ${toX(-2)},${toY(-2.8)}
            ${toX(-2.3)},${toY(-3.2)}`}
          fill="none" stroke={LOCUS_COLOR} strokeWidth="2.5"/>

        {/* Real axis: s=0 to breakaway */}
        <line x1={toX(0)} y1={originY} x2={brkX} y2={brkY} stroke={LOCUS_COLOR} strokeWidth="2.5"/>
        {/* Real axis: s=-2 to breakaway */}
        <line x1={toX(-2)} y1={originY} x2={brkX} y2={brkY} stroke={LOCUS_COLOR} strokeWidth="2.5"/>
        {/* Real axis: s=-2 to break-in */}
        <line x1={toX(-2)} y1={originY} x2={bkiX} y2={bkiY} stroke={LOCUS_COLOR} strokeWidth="2.5"/>
        {/* Real axis: s=-4 to break-in */}
        <line x1={toX(-4)} y1={originY} x2={bkiX} y2={bkiY} stroke={LOCUS_COLOR} strokeWidth="2.5"/>
        {/* Branch 3: s=-4 → -∞ */}
        <line x1={toX(-4)} y1={originY} x2={px+10} y2={originY} stroke={LOCUS_COLOR} strokeWidth="2.5"
          markerEnd="url(#arrRed)"/>

        {/* Arrows on upper/lower branches */}
        <path d={`M ${toX(-1.6)},${toY(2.1)} L ${toX(-1.65)},${toY(2.2)}`}
          stroke={LOCUS_COLOR} strokeWidth="2" markerEnd="url(#arrRed)"/>
        <path d={`M ${toX(-1.6)},${toY(-2.1)} L ${toX(-1.65)},${toY(-2.2)}`}
          stroke={LOCUS_COLOR} strokeWidth="2" markerEnd="url(#arrRed)"/>

        {/* ── OPEN-LOOP POLES ────────────────────────────────────── */}
        <Pole cx={toX(0)} cy={toY(0)} size={15}/>
        <text x={toX(0)+10} y={toY(0)-10} fontSize="10" fill={POLE_COLOR} fontWeight="700">s=0</text>
        <Pole cx={toX(-2)} cy={toY(0)} size={15}/>
        <text x={toX(-2)+10} y={toY(0)-10} fontSize="10" fill={POLE_COLOR} fontWeight="700">s=−2</text>
        <Pole cx={toX(-4)} cy={toY(0)} size={15}/>
        <text x={toX(-4)+10} y={toY(0)-10} fontSize="10" fill={POLE_COLOR} fontWeight="700">s=−4</text>

        {/* ── KEY POINTS ────────────────────────────────────────── */}
        {/* Breakaway */}
        <circle cx={brkX} cy={brkY} r={6} fill="#9b59b6"/>
        <text x={brkX} y={brkY+18} textAnchor="middle" fontSize="10" fill="#9b59b6" fontWeight="700">Breakaway s≈−0.85</text>

        {/* Break-in */}
        <circle cx={bkiX} cy={bkiY} r={6} fill="#9b59b6"/>
        <text x={bkiX} y={bkiY+18} textAnchor="middle" fontSize="10" fill="#9b59b6" fontWeight="700">Break-in s≈−3.15</text>

        {/* Imaginary axis crossing */}
        <circle cx={jcX} cy={jcYp} r={6} fill="#c0392b"/>
        <circle cx={jcX} cy={jcYm} r={6} fill="#c0392b"/>
        <text x={jcX+8} y={jcYp+4} fontSize="10" fill="#c0392b" fontWeight="700">jω≈±j2.83 (K=48)</text>
        <text x={jcX+8} y={jcYp+16} fontSize="9" fill="#c0392b">Gain Margin boundary</text>

        {/* Right panel */}
        <rect x={605} y={py} width={280} height={ph} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={745} y={py+18} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Step-by-Step Solution</text>

        {[
          { y:py+28, bg:'#fff5f5', bc:POLE_COLOR, lines:['Open-loop poles','P = 3:  s = 0, −2, −4','Open-loop zeros: Z = 0'] },
          { y:py+80, bg:'#eaf3ff', bc:'#2E75B6', lines:['Branches & asymptotes','n=3 branches; 3 go to ∞','Angles: 60°, 180°, 300°'] },
          { y:py+132, bg:INFO_ORANGE, bc:ASYM_COLOR, lines:['Centroid','σₐ = (0−2−4)/3 = −2','All 3 asymptotes pass here'] },
          { y:py+184, bg:'#f5eeff', bc:'#9b59b6', lines:['Breakaway point','dK/ds=0 → s ≈ −0.845','K at breakaway ≈ 3.08'] },
          { y:py+236, bg:'#f5eeff', bc:'#9b59b6', lines:['Break-in point','s ≈ −3.155','K at break-in ≈ 3.08'] },
          { y:py+288, bg:'#fff5f5', bc:POLE_COLOR, lines:['jω-axis crossing','Routh-Hurwitz → K_c = 48','Crossing at s = ±j2√2'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={611} y={y} width={268} height={50} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={617} y={y + 15 + i*14} fontSize={i===0?'12':'11'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Full root locus for G(s) = K/[s(s+2)(s+4)]. Three branches start at the open-loop poles (K=0) and go to infinity along asymptotes at 60°, 180°, and 300° centred at σ=−2. The complex branches break away from the real axis at s≈−0.845 and cross the imaginary axis at s=±j2.83 when K=48 — the gain margin boundary.</div>
    </div>
  );
}

// ── Diagram 4: Angle & Magnitude Conditions ───────────────────────
function DiagramConditions() {
  const W = 900, H = 340;
  const px = 60, py = 30, pw = 440, ph = 280;
  const xRange = [-4, 2], yRange = [-2.5, 2.5];
  const toX = r => px + (r - xRange[0]) / (xRange[1] - xRange[0]) * pw;
  const toY = i => py + (1 - (i - yRange[0]) / (yRange[1] - yRange[0])) * ph;
  const originX = toX(0);

  // Test point s = -1 + j1.5
  const ts = { r: -1, i: 1.5 };
  const tx = toX(ts.r), ty = toY(ts.i);

  // Poles at 0, -2, -3; zero at -1
  const pols = [{ r:0,i:0 }, { r:-2,i:0 }, { r:-3,i:0 }];
  const zers = [{ r:-1,i:0 }];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Angle & Magnitude Conditions — Verifying a Point is on the Locus</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>
        <rect x={px} y={py} width={pw} height={ph} rx={4} fill="#fff" stroke="#f0cece"/>
        <SPlaneAxes x0={px} y0={py} w={pw} h={ph} xRange={xRange} yRange={yRange}/>

        {/* Angle lines from poles/zeros to test point */}
        {pols.map((p, i) => (
          <line key={`pl${i}`}
            x1={toX(p.r)} y1={toY(p.i)}
            x2={tx} y2={ty}
            stroke={POLE_COLOR} strokeWidth="1.5" strokeDasharray="5 3" opacity="0.7"/>
        ))}
        {zers.map((z, i) => (
          <line key={`zl${i}`}
            x1={toX(z.r)} y1={toY(z.i)}
            x2={tx} y2={ty}
            stroke={ZERO_COLOR} strokeWidth="1.5" strokeDasharray="5 3" opacity="0.7"/>
        ))}

        {/* Test point */}
        <circle cx={tx} cy={ty} r={7} fill="#27ae60"/>
        <text x={tx+10} y={ty-10} fontSize="11" fill="#27ae60" fontWeight="700">Test point s*</text>
        <text x={tx+10} y={ty+2} fontSize="10" fill="#27ae60">s = −1+j1.5</text>

        {/* Poles */}
        {pols.map((p, i) => <Pole key={i} cx={toX(p.r)} cy={toY(p.i)} size={14}/>)}
        {zers.map((z, i) => <Zero key={i} cx={toX(z.r)} cy={toY(z.i)} size={11}/>)}

        {/* Angle labels near midpoints */}
        <text x={(toX(0)+tx)/2+6}   y={(toY(0)+ty)/2-8}  fontSize="10" fill={POLE_COLOR} fontStyle="italic">θ_p1</text>
        <text x={(toX(-2)+tx)/2+6}  y={(toY(0)+ty)/2-6}  fontSize="10" fill={POLE_COLOR} fontStyle="italic">θ_p2</text>
        <text x={(toX(-3)+tx)/2+6}  y={(toY(0)+ty)/2-6}  fontSize="10" fill={POLE_COLOR} fontStyle="italic">θ_p3</text>
        <text x={(toX(-1)+tx)/2-20} y={(toY(0)+ty)/2+4}  fontSize="10" fill={ZERO_COLOR} fontStyle="italic">θ_z1</text>

        {/* Magnitude lines */}
        <line x1={toX(0)} y1={toY(0)} x2={tx} y2={ty} stroke="#aaa" strokeWidth="1" opacity="0.3"/>

        {/* Info box */}
        <rect x={px+10} y={py+ph-90} width={pw-20} height={82} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={px+20} y={py+ph-74} fontSize="11" fill="#7B0D0D" fontWeight="700">Angle Condition (determines if s* is on locus):</text>
        <text x={px+20} y={py+ph-58} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Σ∠(s*−zᵢ) − Σ∠(s*−pᵢ) = (2k+1) × 180°,  k = 0,±1,±2,…</text>
        <text x={px+20} y={py+ph-38} fontSize="11" fill="#7B0D0D" fontWeight="700">Magnitude Condition (finds gain K at that point):</text>
        <text x={px+20} y={py+ph-22} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">K = Π|s*−pᵢ| / Π|s*−zᵢ|   (product of distances)</text>

        {/* Right explanation panel */}
        <rect x={520} y={py} width={365} height={ph} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={702} y={py+18} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">The Two Root Locus Conditions</text>

        {[
          { y:py+28, bg:'#fff5f5', bc:POLE_COLOR, lines:['Angle Condition','A point s* is on the root locus if and only if','the net angle contribution equals an odd multiple','of 180°. This is the fundamental test.'] },
          { y:py+104, bg:'#eaf3ff', bc:'#2E75B6', lines:['Magnitude Condition','Once a point is confirmed on the locus, the gain','K is found as the ratio of distances from s* to','all poles divided by distances to all zeros.'] },
          { y:py+180, bg:INFO_ORANGE, bc:ASYM_COLOR, lines:['Graphical interpretation','Angles = arctangent of vectors drawn from each','pole/zero to the test point. Magnitudes = lengths','of those vectors (measurable on a scaled plot).'] },
          { y:py+256, bg:'#f5eeff', bc:'#9b59b6', lines:['Design use','Choose desired closed-loop pole location → verify','angle condition → read K from magnitude condition.','This is the basis of gain selection by root locus.'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={526} y={y} width={353} height={72} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={532} y={y + 14 + i*15} fontSize={i===0?'12':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>To test whether a candidate point s* lies on the root locus, compute the net angle from all poles and zeros to that point. If it equals an odd multiple of 180°, the point is on the locus for some positive K. The required gain is then found from the magnitude condition: K equals the product of pole-to-point distances divided by zero-to-point distances.</div>
    </div>
  );
}

// ── Diagram 5: Closed-loop step responses for different K ─────────
function DiagramStepResponses() {
  const W = 900, H = 360;

  // Five gain levels with distinct responses
  const cases = [
    { label:'K = 1', color:'#2E75B6', desc:'Sluggish, overdamped',
      pts:[[0,0],[8,0.15],[18,0.38],[30,0.56],[45,0.70],[65,0.80],[90,0.87],[130,0.93],[180,0.97],[240,0.99]] },
    { label:'K = 5', color:'#27ae60', desc:'Moderate damping ~ζ=0.6',
      pts:[[0,0],[6,0.28],[14,0.68],[22,0.92],[30,1.02],[42,1.05],[60,1.02],[90,1.00],[130,1.00],[240,1.00]] },
    { label:'K = 15', color:ASYM_COLOR, desc:'Light damping ~ζ=0.35',
      pts:[[0,0],[5,0.42],[10,0.90],[16,1.18],[22,1.22],[30,1.12],[42,1.02],[55,0.97],[75,0.99],[130,1.00],[240,1.00]] },
    { label:'K = 30', color:'#9b59b6', desc:'Marginally stable oscillation',
      pts:[[0,0],[4,0.55],[8,1.10],[12,1.38],[18,1.28],[24,0.95],[30,0.72],[38,0.82],[46,1.10],[54,1.30],[62,1.20],[78,0.90],[100,0.92],[130,1.05],[180,1.00],[240,1.00]] },
    { label:'K = 48 (K_c)', color:POLE_COLOR, desc:'Sustained oscillation (GM=0)',
      pts:[[0,0],[4,0.7],[8,1.4],[12,1.7],[18,1.2],[24,0.5],[30,0.3],[36,0.9],[42,1.6],[48,1.7],[54,1.1],[60,0.4],[72,0.4],[84,1.5],[96,1.7],[108,1.0],[120,0.3],[160,0.3],[200,1.5],[240,1.7]] },
  ];

  const bx = 70, by = 30, bw = 580, bh = 290;
  const tMax = 240;
  const toX = t => bx + (t / tMax) * bw;
  const toY = v => by + bh - v * bh * 0.72;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Step Responses for Increasing Gain K — G(s) = K / [s(s+2)(s+4)]</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>
        <rect x={bx} y={by} width={bw} height={bh} rx={4} fill="#fff" stroke="#f0cece"/>

        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1.0, 1.25, 1.5].map(v => (
          <g key={v}>
            <line x1={bx} y1={toY(v)} x2={bx+bw} y2={toY(v)} stroke="#f5f0f0" strokeWidth="1"/>
            <text x={bx-6} y={toY(v)+4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}
        {[0,30,60,90,120,150,180,210,240].map(t => (
          <line key={t} x1={toX(t)} y1={by} x2={toX(t)} y2={by+bh} stroke="#f5f0f0" strokeWidth="1"/>
        ))}

        {/* Reference = 1 */}
        <line x1={bx} y1={toY(1)} x2={bx+bw} y2={toY(1)} stroke="#bbb" strokeWidth="1.5" strokeDasharray="6 4"/>
        <text x={bx+bw-4} y={toY(1)-5} textAnchor="end" fontSize="10" fill="#999">reference = 1</text>

        {/* Axes */}
        <line x1={bx} y1={by+bh} x2={bx+bw} y2={by+bh} stroke="#aaa" strokeWidth="1.4"/>
        <line x1={bx} y1={by} x2={bx} y2={by+bh} stroke="#aaa" strokeWidth="1.4"/>
        <text x={bx+bw/2} y={by+bh+18} textAnchor="middle" fontSize="11" fill="#777">Time →</text>
        <text x={bx-30} y={by+bh/2} textAnchor="middle" fontSize="11" fill="#777" transform={`rotate(-90,${bx-30},${by+bh/2})`}>Output y(t)</text>

        {/* Curves */}
        {cases.map(({ label, color, pts }) => (
          <polyline key={label}
            points={pts.map(([t, v]) => `${toX(t)},${toY(v)}`).join(' ')}
            fill="none" stroke={color} strokeWidth="2.2"/>
        ))}

        {/* Legend inside plot */}
        {cases.map(({ label, color, desc }, i) => (
          <g key={label}>
            <line x1={bx+bw-235} y1={by+16+i*24} x2={bx+bw-210} y2={by+16+i*24} stroke={color} strokeWidth="2.5"/>
            <text x={bx+bw-206} y={by+20+i*24} fontSize="11" fill={color} fontWeight="700">{label}</text>
            <text x={bx+bw-160} y={by+20+i*24} fontSize="10" fill="#666"> — {desc}</text>
          </g>
        ))}

        {/* Right info panel */}
        <rect x={665} y={by} width={220} height={bh} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={775} y={by+18} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Gain vs Response</text>

        {[
          { y:by+28, bg:'#eaf3ff', bc:'#2E75B6', lines:['K = 1','Poles far left; overdamped','Slow but very stable'] },
          { y:by+82, bg:'#f0fef4', bc:'#27ae60', lines:['K = 5','Good PM & GM','Recommended range'] },
          { y:by+136, bg:INFO_ORANGE, bc:ASYM_COLOR, lines:['K = 15','Faster, moderate ringing','Still stable, PM shrinking'] },
          { y:by+190, bg:'#f5eeff', bc:'#9b59b6', lines:['K = 30','High overshoot','GM small; risky'] },
          { y:by+244, bg:'#fff5f5', bc:POLE_COLOR, lines:['K = 48 = K_c','Marginally stable','Poles on imaginary axis'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={671} y={y} width={208} height={50} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={677} y={y+14+i*15} fontSize={i===0?'12':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Step responses as gain K increases from 1 to the critical value K_c=48. Root locus directly predicts this behaviour: poles close to the imaginary axis produce oscillation; at K_c the poles land exactly on the imaginary axis and the response neither decays nor grows. The root locus is a complete map of all possible step responses.</div>
    </div>
  );
}

// ── Diagram 6: Adding poles and zeros — effect on locus shape ─────
function DiagramPoleZeroEffects() {
  const W = 900, H = 380;
  const panels = [
    { x:10,  title:'Adding a Pole', color:'#c0392b',
      desc:['Adds a branch, pulls locus', 'toward the right (destabilises)', 'Asymptotes rotate inward'] },
    { x:310, title:'Adding a Zero', color:'#2E75B6',
      desc:['One fewer branch to ∞','Pulls locus toward the left','Improves stability & PM'] },
    { x:610, title:'Pole-Zero Pair (Lag/Lead)', color:'#27ae60',
      desc:['Near-cancellation near real axis','Net effect: reshape locus','Used in lead-lag compensation'] },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Effect of Adding Poles and Zeros on the Root Locus Shape</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>

        {panels.map(({ x, title, color, desc }, pi) => {
          const pw = 275, ph = 260;
          const cx = x + pw/2, cy = ph/2 + 40;
          const sc = 45;
          const toX = r => cx + r * sc;
          const toY = i => cy - i * sc;

          return (
            <g key={pi}>
              <rect x={x} y={10} width={pw} height={ph+80} rx={5} fill="#fff" stroke="#f0cece"/>
              <text x={x+pw/2} y={32} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{title}</text>

              {/* Mini axes */}
              <line x1={x+14} y1={cy} x2={x+pw-14} y2={cy} stroke="#ddd" strokeWidth="1.3"/>
              <line x1={cx} y1={50} x2={cx} y2={ph+36} stroke="#c0392b" strokeWidth="1.3" strokeDasharray="5 3" opacity="0.4"/>
              <line x1={cx} y1={50} x2={cx} y2={ph+36} stroke="#ddd" strokeWidth="1.3"/>

              {pi === 0 && (
                // Base: two poles at -1, -3; with/without extra pole at -0.5
                <g>
                  {/* Original locus (two poles) — dashed */}
                  <line x1={toX(-1)} y1={cy} x2={toX(-3)} y2={cy} stroke="#bbb" strokeWidth="2" strokeDasharray="4 2"/>
                  <path d={`M ${toX(-2)},${cy} C ${toX(-2)},${toY(1.5)} ${toX(-1.5)},${toY(2.5)} ${toX(-1)},${toY(3)}`}
                    fill="none" stroke="#bbb" strokeWidth="1.5" strokeDasharray="4 2"/>
                  <path d={`M ${toX(-2)},${cy} C ${toX(-2)},${toY(-1.5)} ${toX(-1.5)},${toY(-2.5)} ${toX(-1)},${toY(-3)}`}
                    fill="none" stroke="#bbb" strokeWidth="1.5" strokeDasharray="4 2"/>
                  <text x={toX(-1.2)} y={cy-55} fontSize="9" fill="#aaa">original (2 poles)</text>

                  {/* New locus with extra pole at -0.5 — solid */}
                  <Pole cx={toX(-0.5)} cy={cy} size={14}/>
                  <Pole cx={toX(-1.5)} cy={cy} size={14}/>
                  <Pole cx={toX(-3)} cy={cy} size={14}/>
                  <line x1={toX(-0.5)} y1={cy} x2={toX(-1.5)} y2={cy} stroke={color} strokeWidth="2.5"/>
                  <path d={`M ${toX(-1)},${cy} C ${toX(-0.8)},${toY(1)} ${toX(-0.4)},${toY(2)} ${cx},${toY(2.8)}`}
                    fill="none" stroke={color} strokeWidth="2.5" markerEnd="url(#arrRed)"/>
                  <path d={`M ${toX(-1)},${cy} C ${toX(-0.8)},${toY(-1)} ${toX(-0.4)},${toY(-2)} ${cx},${toY(-2.8)}`}
                    fill="none" stroke={color} strokeWidth="2.5" markerEnd="url(#arrRed)"/>
                  <line x1={toX(-3)} y1={cy} x2={toX(-4.5)} y2={cy} stroke={color} strokeWidth="2.5" markerEnd="url(#arrRed)"/>
                  <text x={toX(-0.5)-5} y={cy-16} fontSize="9" fill={color} fontWeight="700">NEW pole</text>
                  <text x={toX(-0.5)-5} y={cy-4} fontSize="9" fill={color}>pulls toward jω</text>
                </g>
              )}

              {pi === 1 && (
                // Base: three poles; adding a zero pulls locus left
                <g>
                  <Pole cx={toX(0)} cy={cy} size={14}/>
                  <Pole cx={toX(-2)} cy={cy} size={14}/>
                  <Pole cx={toX(-3)} cy={cy} size={14}/>

                  {/* Without zero — locus crosses into RHP */}
                  <path d={`M ${toX(-1)},${cy} C ${toX(-0.5)},${toY(1.5)} ${toX(0.2)},${toY(2.5)} ${toX(0.5)},${toY(3)}`}
                    fill="none" stroke="#bbb" strokeWidth="1.5" strokeDasharray="4 2"/>
                  <path d={`M ${toX(-1)},${cy} C ${toX(-0.5)},${toY(-1.5)} ${toX(0.2)},${toY(-2.5)} ${toX(0.5)},${toY(-3)}`}
                    fill="none" stroke="#bbb" strokeWidth="1.5" strokeDasharray="4 2"/>
                  <text x={toX(0.2)} y={cy-55} fontSize="9" fill="#aaa">without zero</text>

                  {/* Zero added at -1 — locus bends left */}
                  <Zero cx={toX(-1)} cy={cy} size={10}/>
                  <path d={`M ${toX(0)},${cy} C ${toX(-0.5)},${toY(0.5)} ${toX(-1.2)},${toY(1.5)} ${toX(-1.8)},${toY(2.5)}`}
                    fill="none" stroke={ZERO_COLOR} strokeWidth="2.5" markerEnd="url(#arrBlue)"/>
                  <path d={`M ${toX(0)},${cy} C ${toX(-0.5)},${toY(-0.5)} ${toX(-1.2)},${toY(-1.5)} ${toX(-1.8)},${toY(-2.5)}`}
                    fill="none" stroke={ZERO_COLOR} strokeWidth="2.5" markerEnd="url(#arrBlue)"/>
                  <text x={toX(-1)+10} y={cy-16} fontSize="9" fill={ZERO_COLOR} fontWeight="700">NEW zero</text>
                  <text x={toX(-1)+10} y={cy-4} fontSize="9" fill={ZERO_COLOR}>bends locus left</text>
                </g>
              )}

              {pi === 2 && (
                // Lead/lag: pole-zero pair near each other
                <g>
                  <Pole cx={toX(0)} cy={cy} size={14}/>
                  <Pole cx={toX(-2)} cy={cy} size={14}/>

                  {/* Lag: zero closer to origin than pole */}
                  <Zero cx={toX(-0.5)} cy={cy} size={10}/>
                  <Pole cx={toX(-0.1)} cy={cy} size={12}/>
                  <text x={toX(-0.3)} y={cy-16} textAnchor="middle" fontSize="9" fill="#27ae60" fontWeight="700">Lag pair</text>
                  <text x={toX(-0.3)} y={cy-4} textAnchor="middle" fontSize="8" fill="#27ae60">(z&lt;p≈0)</text>

                  {/* Lead: pole closer to origin than zero */}
                  <Zero cx={toX(-1.5)} cy={cy} size={10}/>
                  <Pole cx={toX(-2.5)} cy={cy} size={12}/>
                  <text x={toX(-2)} y={cy-16} textAnchor="middle" fontSize="9" fill="#e07b00" fontWeight="700">Lead pair</text>
                  <text x={toX(-2)} y={cy-4} textAnchor="middle" fontSize="8" fill="#e07b00">(p&lt;z)</text>

                  {/* Locus */}
                  <path d={`M ${toX(0)},${cy} C ${toX(-0.8)},${toY(1)} ${toX(-1.3)},${toY(2)} ${toX(-1.5)},${toY(2.8)}`}
                    fill="none" stroke={color} strokeWidth="2.5" markerEnd="url(#arrGreen)"/>
                  <path d={`M ${toX(0)},${cy} C ${toX(-0.8)},${toY(-1)} ${toX(-1.3)},${toY(-2)} ${toX(-1.5)},${toY(-2.8)}`}
                    fill="none" stroke={color} strokeWidth="2.5" markerEnd="url(#arrGreen)"/>
                  <line x1={toX(-2)} y1={cy} x2={toX(-4)} y2={cy} stroke={color} strokeWidth="2.5" markerEnd="url(#arrGreen)"/>
                </g>
              )}

              {/* Description box */}
              <rect x={x+10} y={ph+42} width={pw-20} height={52} rx={3} fill="#FEF0F0" stroke={color} strokeWidth="1"/>
              {desc.map((t, i) => (
                <text key={i} x={x+18} y={ph+57+i*15} fontSize="11" fill={i===0?color:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Poles attract locus branches (destabilising: the locus bends toward the right-half plane), while zeros repel and attract branches depending on placement (stabilising when placed in the left-half plane). Understanding this is essential for designing compensators: lead compensators add a zero to the left and a pole further left, pulling the locus into the more stable region.</div>
    </div>
  );
}

// ── Diagram 7: Dominant poles & performance specs ─────────────────
function DiagramDominantPoles() {
  const W = 900, H = 360;
  const px = 70, py = 25, pw = 500, ph = 300;
  const xRange = [-6, 2], yRange = [-4, 4];
  const toX = r => px + (r - xRange[0]) / (xRange[1] - xRange[0]) * pw;
  const toY = i => py + (1 - (i - yRange[0]) / (yRange[1] - yRange[0])) * ph;
  const originX = toX(0);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Dominant Poles — Translating Performance Specs into the s-Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>
        <rect x={px} y={py} width={pw} height={ph} rx={4} fill="#fff" stroke="#f0cece"/>
        <rect x={originX} y={py} width={px+pw-originX} height={ph} fill="#ffebee" opacity="0.25"/>

        <SPlaneAxes x0={px} y0={py} w={pw} h={ph} xRange={xRange} yRange={yRange}/>

        {/* Desired region: ζ>0.5, ωₙ>2, σ<-1 */}
        {/* Shade the "desired region" in pale green */}
        <clipPath id="plotClip">
          <rect x={px} y={py} width={pw} height={ph}/>
        </clipPath>

        {/* Constant ζ=0.5 ray */}
        {(() => {
          const angle = Math.acos(0.5); // 60°
          const len = 4.5;
          const dx = -Math.cos(angle) * len, dy = Math.sin(angle) * len;
          return (
            <>
              <line x1={originX} y1={toY(0)} x2={toX(dx)} y2={toY(dy)}
                stroke="#27ae60" strokeWidth="1.8" strokeDasharray="7 3" opacity="0.7"/>
              <line x1={originX} y1={toY(0)} x2={toX(dx)} y2={toY(-dy)}
                stroke="#27ae60" strokeWidth="1.8" strokeDasharray="7 3" opacity="0.7"/>
              <text x={toX(dx)-14} y={toY(dy)-6} fontSize="10" fill="#27ae60" fontWeight="700">ζ=0.5 (min)</text>
            </>
          );
        })()}

        {/* ωₙ = 2 semicircle */}
        <path d={`M ${toX(0)},${toY(-2)} A ${toX(2)-originX},${toY(0)-toY(2)} 0 0,0 ${toX(0)},${toY(2)}`}
          fill="none" stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="7 3" opacity="0.7"/>
        <text x={toX(-1.7)} y={toY(1.4)} fontSize="10" fill="#2E75B6" fontWeight="700">ωₙ=2 (min)</text>

        {/* σ = -1 vertical line */}
        <line x1={toX(-1)} y1={py} x2={toX(-1)} y2={py+ph}
          stroke={ASYM_COLOR} strokeWidth="1.8" strokeDasharray="7 3" opacity="0.7"/>
        <text x={toX(-1)+4} y={py+14} fontSize="10" fill={ASYM_COLOR} fontWeight="700">σ=−1 (min decay)</text>

        {/* Acceptable region fill (approximate polygon) */}
        <polygon
          points={`${toX(-1)},${toY(0)} ${toX(-2)},${toY(0)} ${toX(-3.5)},${toY(0)} ${toX(-3.5)},${toY(3)} ${toX(-1.7)},${toY(3)} ${toX(-1)},${toY(1.73)}`}
          fill="#27ae60" opacity="0.08" clipPath="url(#plotClip)"/>
        <polygon
          points={`${toX(-1)},${toY(0)} ${toX(-2)},${toY(0)} ${toX(-3.5)},${toY(0)} ${toX(-3.5)},${toY(-3)} ${toX(-1.7)},${toY(-3)} ${toX(-1)},${toY(-1.73)}`}
          fill="#27ae60" opacity="0.08" clipPath="url(#plotClip)"/>
        <text x={toX(-2.8)} y={toY(1.5)} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700" opacity="0.7">DESIRED</text>
        <text x={toX(-2.8)} y={toY(1.5)+14} textAnchor="middle" fontSize="10" fill="#27ae60" opacity="0.7">REGION</text>

        {/* Dominant poles at desired location */}
        <circle cx={toX(-1.5)} cy={toY(2.6)} r={7} fill="#c0392b"/>
        <circle cx={toX(-1.5)} cy={toY(-2.6)} r={7} fill="#c0392b"/>
        <text x={toX(-1.5)+10} y={toY(2.6)-6} fontSize="10" fill="#c0392b" fontWeight="700">Desired dominant poles</text>
        <text x={toX(-1.5)+10} y={toY(2.6)+6} fontSize="10" fill="#c0392b">s = −1.5 ± j2.6</text>
        <text x={toX(-1.5)+10} y={toY(2.6)+18} fontSize="9" fill="#888">ζ≈0.5, ωₙ≈3.0</text>

        {/* Far pole (non-dominant) */}
        <Pole cx={toX(-5)} cy={toY(0)} size={13}/>
        <text x={toX(-5)} y={toY(0)-16} textAnchor="middle" fontSize="9" fill="#aaa">Non-dominant pole</text>
        <text x={toX(-5)} y={toY(0)-4} textAnchor="middle" fontSize="9" fill="#aaa">(5× further left)</text>

        {/* Right panel */}
        <rect x={585} y={py} width={300} height={ph} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={735} y={py+18} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Spec → s-Plane</text>

        {[
          { y:py+26, bg:'#fff5f5', bc:POLE_COLOR, lines:['% Overshoot → ζ','%OS = e^{−πζ/√(1−ζ²)} × 100','%OS=16%→ζ≈0.5; 5%→ζ≈0.69'] },
          { y:py+82, bg:'#eaf3ff', bc:'#2E75B6', lines:['Settling time → σ','ts ≈ 4/(ζωₙ) = 4/|Re(s)|','ts=2s → Re(s) < −2'] },
          { y:py+138, bg:INFO_ORANGE, bc:ASYM_COLOR, lines:['Rise time → ωₙ','tr ≈ 1.8/ωₙ  (approx)','tr=0.5s → ωₙ > 3.6 rad/s'] },
          { y:py+194, bg:'#f5eeff', bc:'#9b59b6', lines:['Dominant pole rule','Non-dominant poles should be','5–10× further left in s-plane','so their transient decays fast'] },
          { y:py+262, bg:'#fff5f5', bc:POLE_COLOR, lines:['Design flow','1. Convert specs to s-plane region','2. Pick desired pole location','3. Find K via magnitude condition'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={591} y={y} width={288} height={lines.length<=3?50:60} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {lines.map((t, i) => (
              <text key={i} x={597} y={y+14+i*15} fontSize={i===0?'12':'10'} fill={i===0?bc:'#444'} fontWeight={i===0?'700':'400'}>{t}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Performance specifications (overshoot, settling time, rise time) translate directly into regions of the s-plane. The desired dominant poles must lie inside all three constraint boundaries. Non-dominant poles are placed at least 5× further from the imaginary axis so they have negligible effect on the transient response. The root locus is then used to find the gain K that places the closed-loop poles at the desired location.</div>
    </div>
  );
}

// ── Diagram 8: jω-axis crossing via Routh–Hurwitz ────────────────
function DiagramRouthCrossing() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Finding the jω-Axis Crossing — Routh–Hurwitz Method</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <SvgDefs/>

        {/* Example: G(s)=K/[s(s+2)(s+4)] → CE: s³+6s²+8s+K=0 */}
        <rect x={10} y={10} width={880} height={290} rx={5} fill="#fff" stroke="#f0cece"/>

        <text x={450} y={34} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">
          Example: G(s) = K / [s(s+2)(s+4)]  →  Characteristic Equation: s³ + 6s² + 8s + K = 0
        </text>

        {/* Routh array */}
        {[
          ['s³', '1', '8', '—', '#fff5f5', POLE_COLOR],
          ['s²', '6', 'K', '—', '#fff', '#f0cece'],
          ['s¹', '(48−K)/6', '0', '—', '#fff7e6', ASYM_COLOR],
          ['s⁰', 'K', '—', '—', '#fff5f5', POLE_COLOR],
        ].map(([row, c1, c2, c3, bg, bc], ri) => {
          const rowY = 50 + ri * 48;
          return (
            <g key={ri}>
              {/* Row label */}
              <rect x={20} y={rowY} width={50} height={42} rx={2} fill={bc} opacity="0.15"/>
              <text x={45} y={rowY+26} textAnchor="middle" fontSize="14" fill={bc} fontWeight="700" fontFamily="'Courier New',monospace">{row}</text>
              {/* Cells */}
              {[c1, c2, c3].map((cell, ci) => (
                <g key={ci}>
                  <rect x={76+ci*150} y={rowY} width={148} height={42} rx={2} fill={bg} stroke={bc} strokeWidth="0.5"/>
                  <text x={76+ci*150+74} y={rowY+26} textAnchor="middle" fontSize="13"
                    fill={ci===0?bc:'#333'} fontWeight={ci===0?'600':'400'} fontFamily="'Courier New',monospace">{cell}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Stability conditions from Routh */}
        <rect x={490} y={50} width={385} height={192} rx={5} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={682} y={70} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Stability Conditions</text>

        {[
          { y:82, color:'#27ae60', text:'Condition 1: K > 0  (from s⁰ row, first element > 0)' },
          { y:104, color:'#27ae60', text:'Condition 2: (48 − K)/6 > 0  →  K < 48  (from s¹ row)' },
          { y:126, color:POLE_COLOR, text:'Therefore stable for:  0 < K < 48' },
          { y:154, color:ASYM_COLOR, text:'At K = 48: s¹ row → 0  (auxiliary equation from s² row)' },
          { y:174, color:ASYM_COLOR, text:'Auxiliary eqn:  6s² + 48 = 0  →  s² = −8' },
          { y:194, color:ASYM_COLOR, text:'jω-axis crossing:  s = ±j√8 = ±j2√2 ≈ ±j2.83' },
          { y:220, color:'#2E75B6', text:'This matches the root locus crossing seen in the plot.' },
        ].map(({ y, color, text }) => (
          <text key={y} x={500} y={y} fontSize="11.5" fill={color} fontFamily="'Courier New',monospace">{text}</text>
        ))}

        {/* Caption box */}
        <rect x={20} y={252} width={460} height={46} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={30} y={268} fontSize="11" fill="#2E75B6" fontWeight="700">Routh–Hurwitz procedure:</text>
        <text x={30} y={283} fontSize="10" fill="#444">Fill the array row by row from the characteristic polynomial coefficients.</text>
        <text x={30} y={295} fontSize="10" fill="#444">Number of RHP poles = number of sign changes in the first column.</text>
      </svg>
      <div style={S.dCaption}>The Routh–Hurwitz criterion provides the exact gain range for stability and the jω-axis crossing frequency algebraically, without needing to factorise the characteristic polynomial. The auxiliary equation (formed from the row just above the zero row) gives the crossing frequencies, which match the root locus imaginary-axis crossings.</div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────
export default function RootLocus() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>The Root Locus Method</h1>
      <p style={S.p}>
        The Root Locus method, developed by Walter R. Evans in 1948, is one of the most powerful graphical techniques in classical control theory. It answers a deceptively simple question: <em>as the gain K of a feedback control system increases from zero to infinity, where do the closed-loop poles go?</em> Since closed-loop poles determine stability and transient behaviour, tracking their movement with gain gives a complete picture of what every possible gain setting will do — and allows a designer to choose K to meet precise performance specifications.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Root locus = locus of closed-loop poles in the s-plane as gain K varies from 0 to +∞",
        "Branches start at open-loop poles (K=0) and end at open-loop zeros or infinity (K→∞)",
        "Number of branches = number of open-loop poles; conjugate pairs are symmetric about the real axis",
        "Stability: poles in left-half plane (Re(s)<0) → stable; right-half plane → unstable",
        "Real-axis rule: segment is on the locus if an ODD number of real poles+zeros lie to its right",
        "Asymptotes give the directions branches travel to infinity; centroid σₐ = (Σpoles−Σzeros)/(P−Z)",
        "Breakaway/break-in points: where locus leaves or re-enters the real axis (dK/ds = 0)",
        "Routh–Hurwitz gives the exact gain K where branches cross the imaginary axis",
        "Performance specs (overshoot, settling time) translate directly to desired regions of the s-plane",
      ]}/>

      <h2 style={S.h2}>The s-Plane — Foundation of Root Locus</h2>
      <p style={S.p}>
        The complex s-plane is the arena in which root locus operates. Every point s = σ + jω represents a possible mode of the system: the real part σ governs exponential growth or decay, and the imaginary part ω governs oscillation frequency. The root locus plots exactly how closed-loop poles — points in this plane — move as gain changes.
      </p>
      <DiagramSPlaneAnatomy/>
      <Table2 rows={[
        ["s-plane","Complex plane of the Laplace variable s = σ + jω\nLeft-half plane (σ<0): stable modes; right-half plane (σ>0): unstable modes"],
        ["Closed-loop poles","Roots of 1 + KG(s)H(s) = 0  (the characteristic equation)\nDetermine stability, natural frequency, and damping of the system"],
        ["Open-loop poles","Roots of the denominator of G(s)H(s); marked × on the plot\nLocus branches start here at K=0"],
        ["Open-loop zeros","Roots of the numerator of G(s)H(s); marked ○ on the plot\nLocus branches end here as K→∞"],
        ["Damping ratio ζ","Lines of constant ζ radiate from the origin at angle ±cos⁻¹(ζ)\nζ=1: critically damped (real axis); ζ=0: undamped (imaginary axis)"],
        ["Natural frequency ωₙ","Semicircles of constant ωₙ centred at the origin\nωₙ = distance from origin to pole: √(σ²+ω²)"],
        ["Damped frequency ωd","ωd = ωₙ√(1−ζ²) = imaginary part of complex conjugate pole pair\nFrequency of the oscillatory component of the step response"],
      ]}/>

      <h2 style={S.h2}>The Five Construction Rules</h2>
      <p style={S.p}>
        Evans showed that the root locus can be sketched precisely using five rules derived from the angle and magnitude conditions. These rules allow engineers to draw an accurate locus by hand — an invaluable skill for understanding how system parameters affect stability before reaching for numerical software.
      </p>
      <DiagramConstructionRules/>
      <Table2 rows={[
        ["Rule 1 — Start/End","Locus starts at open-loop poles (K=0) and ends at open-loop zeros (K→∞)\nBranches that cannot reach a finite zero go to infinity along asymptotes"],
        ["Rule 2 — Number of branches","# branches = # open-loop poles P\nBranches are continuous; complex branches occur in conjugate pairs (real-axis symmetry)"],
        ["Rule 3 — Real-axis segments","A point on the real axis is on the locus if the total number of\nreal open-loop poles AND zeros to its RIGHT is odd"],
        ["Rule 4 — Asymptotes","P−Z branches travel to ∞; asymptote angles = (2k+1)×180°/(P−Z), k=0,1,…\nAll asymptotes emanate from centroid: σₐ = (Σpoles − Σzeros)/(P−Z)"],
        ["Rule 5 — Breakaway/Break-in","Breakaway: locus leaves real axis (find max K on real-axis segment)\nBreak-in: locus returns to real axis; both solved by dK/ds = 0"],
        ["Rule 6 — Departure angles","Angle of departure from a complex pole p_k:\nφ_dep = 180° + Σ∠(p_k−zᵢ) − Σ∠(p_k−pⱼ) for j≠k"],
        ["Rule 7 — Arrival angles","Angle of arrival at a complex zero z_k:\nφ_arr = 180° − Σ∠(z_k−pᵢ) + Σ∠(z_k−zⱼ) for j≠k"],
        ["Routh-Hurwitz for jω crossings","Substitute s=jω into the characteristic equation, separate real/imaginary parts\nOr: use Routh array — when a row goes to zero, the auxiliary polynomial gives crossing freq."],
      ]}/>

      <h2 style={S.h2}>Worked Example — G(s) = K / [s(s+2)(s+4)]</h2>
      <p style={S.p}>
        This classic third-order system illustrates every construction rule. Three poles at 0, −2, and −4; no finite zeros. Three branches must go to infinity, generating three asymptotes. The locus has one real branch that goes to −∞ and two complex branches that arc through the left-half plane and eventually cross the imaginary axis at a computable gain.
      </p>
      <DiagramWorkedExample/>

      <h2 style={S.h2}>The Angle and Magnitude Conditions</h2>
      <p style={S.p}>
        The root locus is defined mathematically by two conditions that must be satisfied simultaneously. The angle condition determines <em>which</em> points in the s-plane lie on the locus; the magnitude condition determines <em>what gain K</em> is required at each such point. Together they give the complete map.
      </p>
      <DiagramConditions/>
      <Table2 rows={[
        ["Closed-loop char. eqn.","1 + KG(s)H(s) = 0  →  G(s)H(s) = −1/K\nFor positive K: |G(s)H(s)| = 1/K and ∠G(s)H(s) = (2k+1)×180°"],
        ["Angle condition","Σ∠(s−zᵢ) − Σ∠(s−pᵢ) = (2k+1)×180°  for k = 0, ±1, ±2, …\nThis is the test: satisfied → point is on the locus for some K≥0"],
        ["Magnitude condition","K = Π|s−pᵢ| / Π|s−zᵢ|\nProduct of distances from s to all poles divided by distances to all zeros"],
        ["Graphical evaluation","Draw vectors from each pole and zero to the candidate point s*\nAngles = arctan(Im/Re) of each vector; lengths = Euclidean distances"],
        ["Negative gain locus (K<0)","Angle condition becomes even multiples of 180°: ∠G(s)H(s) = 2k×180°\nGives complementary locus (180° root locus) — segments swap with positive locus"],
        ["Checking a design","Choose desired s*, check angle condition, then read K from magnitude condition\nThis directly gives the gain required to place poles at s*"],
      ]}/>

      <h2 style={S.h2}>Step Response vs. Gain</h2>
      <p style={S.p}>
        The root locus directly predicts how the closed-loop step response changes with gain. As poles move along the locus with increasing K, the transient response transitions from overdamped to underdamped to oscillatory. Reading the locus lets a designer choose K before running a single simulation.
      </p>
      <DiagramStepResponses/>
      <Table2 rows={[
        ["Overdamped (K small)","Two real poles; exponential decay with no overshoot\nSlow response; poles far left on real axis; high stability margins"],
        ["Critically damped","Repeated real poles (at breakaway point)\nFastest response without overshoot; K = K_breakaway"],
        ["Underdamped","Complex conjugate poles in LHP; oscillatory step response\nDamping ratio ζ = |Re(s)|/ωₙ; PM ≈ 100ζ degrees (approx)"],
        ["Marginally stable","Poles on imaginary axis (K = K_c)\nSustained sinusoidal oscillation; gain margin = 0 dB"],
        ["Unstable","Poles in right-half plane (K > K_c)\nStep response grows without bound; system diverges"],
        ["Bandwidth vs gain","Increasing K moves poles further from origin → higher bandwidth → faster rise time\nBut also reduces damping → more overshoot — the fundamental design trade-off"],
      ]}/>

      <h2 style={S.h2}>Effect of Poles and Zeros on the Locus</h2>
      <p style={S.p}>
        Adding poles and zeros to the open-loop transfer function reshapes the root locus — and this is precisely how compensators are designed. A pole pulls locus branches toward the right (toward instability); a zero attracts them to the left (toward stability). Understanding this attraction-repulsion geometry is the key to compensator design by root locus.
      </p>
      <DiagramPoleZeroEffects/>
      <Table2 rows={[
        ["Adding a pole (open-loop)","Adds one more branch; asymptote count increases; centroid shifts right\nGeneral effect: locus moves toward RHP — system harder to stabilise"],
        ["Adding a zero (open-loop)","Reduces asymptote count by 1; centroid shifts left\nGeneral effect: locus moves toward LHP — improves stability margins"],
        ["Lead compensator","C(s) = K(s+z)/(s+p), p > z > 0  (zero closer to origin)\nAdds phase lead near crossover; improves PM; adds zero to pull locus left"],
        ["Lag compensator","C(s) = K(s+z)/(s+p), z > p > 0  (pole closer to origin)\nReduces high-frequency gain; improves steady-state error; slight PM reduction"],
        ["PD controller","C(s) = Kp(1 + Td·s) — adds a zero at s = −1/Td\nPulls locus to the left; faster response with better damping"],
        ["PID controller","Adds both a zero (derivative) and a pole at origin (integral)\nIntegral → zero steady-state error; derivative → improved transient; pole at 0 → extra branch"],
        ["Notch filter","Pair of zeros near resonant frequency + stabilising poles further left\nCancels or attenuates resonance peak without disturbing locus elsewhere"],
      ]}/>

      <h2 style={S.h2}>Dominant Poles and Performance Specifications</h2>
      <p style={S.p}>
        The most powerful application of root locus is translating time-domain performance specifications — overshoot, settling time, rise time — directly into desired closed-loop pole locations in the s-plane. The designer then uses the locus to find the gain K (and optionally the compensator) that places poles in the desired region.
      </p>
      <DiagramDominantPoles/>
      <Table2 rows={[
        ["Dominant pole assumption","If non-dominant poles are ≥5× further left than dominant poles,\nthe system behaves approximately like a 2nd-order system — simple formulas apply"],
        ["% Overshoot → ζ","%OS = 100 × e^{−πζ/√(1−ζ²)}\n%OS=5%→ζ≈0.69; 16%→ζ≈0.50; 25%→ζ≈0.40"],
        ["Settling time → σ","ts ≈ 4/(ζωₙ) = 4/|Re(s*)|\nSpecify ts → required Re(s*) < −4/ts"],
        ["Peak time → ωd","tp = π/ωd = π/|Im(s*)|\nSpecify tp → required Im(s*) > π/tp"],
        ["Rise time → ωn","tr ≈ (1.8)/ωₙ  (rough approximation)\nSpecify tr → required ωₙ > 1.8/tr"],
        ["Design flow","1. Convert all specs to constraints in s-plane\n2. Identify feasible region intersection\n3. Pick desired s* inside region\n4. Check angle condition; adjust compensator if needed\n5. Find K from magnitude condition"],
      ]}/>

      <h2 style={S.h2}>jω-Axis Crossing — Routh–Hurwitz Criterion</h2>
      <p style={S.p}>
        The imaginary-axis crossing is the most critical point on the root locus — it marks the exact gain at which the system transitions from stable to unstable. The Routh–Hurwitz criterion finds this crossing algebraically from the characteristic polynomial, without solving for roots explicitly.
      </p>
      <DiagramRouthCrossing/>
      <Table2 rows={[
        ["Characteristic polynomial","CE: 1 + KG(s)H(s) = 0  →  write as polynomial in s with K as parameter\nCoefficients are functions of K; Routh array is built from these"],
        ["Routh array","Arrange coefficients in two rows, then compute subsequent rows by cross-multiplication\nFirst column signs give stability; zero row signals marginal stability"],
        ["Critical gain K_c","Value of K that makes a first-column entry zero (entire row)\nAt K_c the locus crosses the imaginary axis — this is the gain margin boundary"],
        ["Auxiliary equation","Formed from the row just above the zero row: gives s² = −ω²\nSolving gives the crossing frequency ω_cross = √(−auxiliary root)"],
        ["Gain margin from K_c","GM = K_c / K_design  (ratio)  or  GM(dB) = 20 log₁₀(K_c/K_design)\nDirectly links root locus and Bode/Nyquist margins"],
        ["Multiple crossings","Locus can cross imaginary axis more than once (conditionally stable systems)\nRouth finds all such K values; check each for crossing direction"],
      ]}/>

      <h2 style={S.h2}>Summary: Root Locus Design Procedure</h2>
      <Table2 rows={[
        ["Step 1 — Find open-loop TF","Write G(s)H(s) in factored form\nIdentify all poles (×) and zeros (○); count P and Z"],
        ["Step 2 — Sketch locus","Apply Rules 1–5: real-axis segments, asymptotes (angle + centroid), breakaway points\nMark jω crossing using Routh-Hurwitz"],
        ["Step 3 — Translate specs","Convert %OS → ζ, ts → σ, tp → ωd\nMark the desired-pole region on the s-plane sketch"],
        ["Step 4 — Check feasibility","Does the root locus pass through the desired region?\nIf not: add lead/lag/PD compensator to reshape the locus"],
        ["Step 5 — Find gain K","Apply magnitude condition at desired pole s*:\nK = Π|s*−pᵢ| / Π|s*−zᵢ|"],
        ["Step 6 — Verify","Compute all closed-loop poles (not just dominant pair)\nCheck non-dominant poles are sufficiently far left; simulate step response to confirm"],
        ["Numerical tools","MATLAB: rlocus(sys), rlocfind(sys) for interactive gain selection\nSciPy/Python: control.root_locus(); also available in GNU Octave and Simulink"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Char. equation: 1 + KG(s)H(s) = 0  →  G(s)H(s) = −1/K",
        "Angle condition: Σ∠(s−zᵢ) − Σ∠(s−pᵢ) = (2k+1)×180°  (determines if s is on locus)",
        "Magnitude condition: K = Π|s−pᵢ| / Π|s−zᵢ|  (finds gain at any locus point)",
        "Asymptote angles: θₖ = (2k+1)×180° / (P−Z),  k = 0, 1, …, (P−Z−1)",
        "Asymptote centroid: σₐ = (Σ Re(poles) − Σ Re(zeros)) / (P−Z)",
        "Breakaway/break-in: solve Σ 1/(s−pᵢ) = Σ 1/(s−zᵢ)  or  dK/ds = 0",
        "Overshoot → damping: %OS = 100 e^{−πζ/√(1−ζ²)}  |  ζ ≈ −ln(%OS/100) / √(π²+ln²(%OS/100))",
        "Settling time: ts ≈ 4/(ζωₙ)  |  Rise time: tr ≈ 1.8/ωₙ  |  Peak time: tp = π/ωd",
        "Gain margin: GM = K_critical / K_design  (find K_critical from Routh–Hurwitz zero row)",
      ]}/>
    </div>
  );
}